// Coordinates the GAM SPA refresh between the ad components (AwGoogleAd) and the
// navigation plugin (aw-gam.client.ts).
//
// Why this exists — the timing problem it solves:
// With `out-in` page transitions the NEW page mounts while the OLD page is still
// animating out, so the leaving page's ad-slot divs stay in the DOM for a while
// AFTER the route change. The AdsInteractive wrapper's `spaHardReset()` decides
// per slot id by `document.getElementById`: if a slot div is present it refreshes
// THAT one immediately and stops watching for it. So if a slot id is reused
// across the two pages (e.g. a 970x250 leaderboard on both), refreshing too early
// hits the OLD, leaving div and never re-targets the new page's fresh div — the
// shared ad stays blank.
//
// The reliable signal that the old page's slot divs are gone is the ad
// components' `unmounted`. AwGoogleAd does NOT unmount on scroll (its
// `aw-if-visible` wrapper is `:disabled`), so an unmount means a real page
// change. We debounce the unmount burst and fire a single reset once it settles,
// and only if a navigation happened since the last reset.

/** The decision logic, isolated from Nuxt/DOM so it can be unit-tested. */
export interface GamSpaRefreshCoordinator {
  /** Call on each SPA route change (after skipping the initial one). */
  onNavigation: () => void;
  /** Call from each ad component's `onMounted`. */
  onAdMount: () => void;
  /** Call from each ad component's `onUnmounted`. */
  onAdUnmount: () => void;
}

// Opaque timer handle: the coordinator only stores it and hands it back to
// clearTimer, so its concrete type (browser `number` vs Node `Timeout`) is
// irrelevant — keeping it opaque avoids the lib-dependent `setTimeout` return
// type leaking into the handle field.
type TimerHandle = ReturnType<typeof setTimeout>;

interface CoordinatorDeps {
  /** Runs the actual GAM refresh (spaHardReset / pubads().refresh()). */
  refresh: () => void;
  /** Debounce timer setter — injectable for tests. Defaults to setTimeout. */
  setTimer?: (cb: () => void, ms: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle) => void;
  /** Debounce window collapsing the leaving page's unmount burst, in ms. */
  debounceMs?: number;
}

/**
 * Builds the navigation/unmount → debounced-refresh state machine.
 *
 * The refresh must run only once the LEAVING page's ad-slot divs have left the
 * DOM (otherwise spaHardReset latches onto the stale leaving div of a reused
 * slot id and never re-targets the new page's fresh div). The catch is that the
 * unmount-vs-navigation order — and the gap between them — differs by navigation
 * type, so we gate on a live ad-component count rather than on a fixed delay:
 *
 *  - DIFFERENT route components, `out-in` transition (e.g. product-list → home):
 *    nav fires FIRST, the new page mounts, and the old ads unmount ~1.8s LATER.
 *    At nav time leaving ads are still live (`liveCount > 0`), so we do NOT
 *    refresh yet — we wait for their unmount, by which point the stale divs are
 *    gone.
 *  - SAME route component, content swapped via `<component :is>` (e.g.
 *    CMS ↔ product-list, both on `[...slug].vue`): the old ads unmount BEFORE
 *    the route changes, so at nav time `liveCount === 0` — the leaving divs are
 *    already gone and the navigation is the only remaining trigger, so we
 *    refresh now.
 *
 * In both cases the refresh is debounced (to coalesce a multi-slot unmount
 * burst) and `refreshOwed` is cleared on fire, so scroll / initial load never
 * refresh and a navigation never fires more than once (no double impressions).
 */
export function createGamSpaRefreshCoordinator (deps: CoordinatorDeps): GamSpaRefreshCoordinator {
  const setTimer: NonNullable<CoordinatorDeps['setTimer']> = deps.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimer: NonNullable<CoordinatorDeps['clearTimer']> = deps.clearTimer ?? (handle => clearTimeout(handle));
  const debounceMs = deps.debounceMs ?? 100;

  // Number of currently mounted ad components. Lets us tell, at navigation time,
  // whether the leaving page's ads are already gone (fire now) or still present
  // (wait for their unmount).
  let liveCount = 0;
  let refreshOwed = false;
  let debounceHandle: TimerHandle | null = null;

  function scheduleRefresh () {
    if (!refreshOwed) {
      return;
    }
    if (debounceHandle !== null) {
      clearTimer(debounceHandle);
    }
    debounceHandle = setTimer(() => {
      debounceHandle = null;
      if (!refreshOwed) {
        return;
      }
      refreshOwed = false;
      deps.refresh();
    }, debounceMs);
  }

  return {
    onAdMount () {
      liveCount += 1;
    },
    onAdUnmount () {
      liveCount = Math.max(0, liveCount - 1);
      // A leaving-page ad has now left the DOM. If a navigation is owed, this is
      // the moment to refresh (the out-in / different-route case, where the
      // unmount lands after the navigation).
      if (refreshOwed) {
        scheduleRefresh();
      }
    },
    onNavigation () {
      refreshOwed = true;
      // If the leaving page's ads already unmounted before the route changed
      // (same-route content swap), nothing else will trigger us — refresh now.
      // Otherwise leaving ads are still mounted; wait for their unmount so we
      // don't refresh against the stale leaving div.
      if (liveCount === 0) {
        scheduleRefresh();
      }
    },
  };
}

// --- Singleton wiring used by the live plugin/component -----------------------

interface AwGamAdLifecycleListener {
  onAdMount: () => void;
  onAdUnmount: () => void;
}

let lifecycleListener: AwGamAdLifecycleListener | null = null;

/**
 * Registered by the navigation plugin; receives ad-component mount/unmount so
 * it can drive the refresh coordinator. Only one listener exists (the singleton
 * plugin).
 */
export function setAwGamAdLifecycleListener (fn: AwGamAdLifecycleListener | null): void {
  lifecycleListener = fn;
}

/** Called by AwGoogleAd from its `onMounted`. */
export function notifyAwGamMount (): void {
  lifecycleListener?.onAdMount();
}

/** Called by AwGoogleAd from its `onUnmounted`. */
export function notifyAwGamUnmount (): void {
  lifecycleListener?.onAdUnmount();
}
